'use client'

import { Component } from 'react'
import StackTrace from 'stacktrace-js'

class ErrorBoundary extends Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        // Check for chunk load errors (stale code after deploy)
        const isChunkError = error?.name === 'ChunkLoadError' ||
            error?.message?.includes('Loading chunk') ||
            error?.message?.includes('Failed to fetch dynamically imported module')

        if (isChunkError && typeof window !== 'undefined') {
            // Auto-reload to get fresh code
            window.location.reload()
            return { hasError: false, error: null }
        }

        return { hasError: true, error }
    }

    componentDidCatch(error, errorInfo) {
        // Send error to our logging endpoint
        this.logError(error, errorInfo)
    }

    /**
     * Format decoded stack frames into readable string
     */
    formatStackFrames(frames) {
        return frames.map(frame => {
            const fileName = frame.fileName || 'unknown'
            const lineNumber = frame.lineNumber || '?'
            const columnNumber = frame.columnNumber || '?'
            const functionName = frame.functionName || '(anonymous)'
            return `    at ${functionName} (${fileName}:${lineNumber}:${columnNumber})`
        }).join('\n')
    }

    /**
     * Decode minified stack trace using source maps
     */
    async getDecodedStack(error) {
        if (!error) return 'No error object available'

        try {
            const frames = await StackTrace.fromError(error)
            return this.formatStackFrames(frames)
        } catch (e) {
            // Fallback to raw stack if source map decoding fails
            console.warn('Failed to decode stack trace:', e)
            return error.stack || 'Stack trace unavailable'
        }
    }

    async logError(error, errorInfo) {
        try {
            // Decode the minified stack trace using source maps
            const decodedStack = await this.getDecodedStack(error)

            await fetch('/api/log-client-error', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: error.message,
                    stack: decodedStack,
                    rawStack: error.stack,
                    componentStack: errorInfo?.componentStack,
                    url: typeof window !== 'undefined' ? window.location.href : '',
                    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
                    timestamp: new Date().toISOString(),
                    type: 'react'
                })
            })
        } catch (e) {
            console.error('Failed to log error:', e)
        }
    }

    render() {
        if (this.state.hasError) {
            // Render fallback UI
            return (
                <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
                    <h2 className="text-2xl font-light mb-4">Something went wrong</h2>
                    <p className="text-light mb-6">
                        We've been notified and are looking into it.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="btn btn-primary"
                    >
                        Reload Page
                    </button>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
